import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_URL } from '../config/api';

interface LoadingPageProps {
  submissionData?: any;
}

const LoadingPage: React.FC<LoadingPageProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loadingText, setLoadingText] = useState('Processing your submission...');
  const [progress, setProgress] = useState(0);

  const submissionData = location.state?.submissionData;

  useEffect(() => {
    if (!submissionData) {
      navigate('/inquiry/ssemble');
      return;
    }

    const processSubmission = async () => {
      try {
        // 진행률 애니메이션
        const progressInterval = setInterval(() => {
          setProgress(prev => {
            if (prev < 90) return prev + Math.random() * 15;
            return prev;
          });
        }, 200);

        // 로딩 텍스트 변경
        const textMessages = [
          'Processing your submission...',
          'Analyzing your profile...',
          'Calculating metrics...',
          'Determining collaboration status...',
          'Almost done...'
        ];

        let textIndex = 0;
        const textInterval = setInterval(() => {
          textIndex = (textIndex + 1) % textMessages.length;
          setLoadingText(textMessages[textIndex]);
        }, 1500);

        // 실제 API 호출
        const response = await fetch(`${API_URL}/api/responses/inquiry`, {
          method: 'POST',
          body: submissionData
        });

        const result = await response.json();
        
        // 진행률 완료
        setProgress(100);
        clearInterval(progressInterval);
        clearInterval(textInterval);

        // 잠시 대기 후 결과 처리
        setTimeout(() => {
          if (response.status === 409) {
            // 중복 제출
            navigate('/inquiry/ssemble', {
              state: { error: result.error || 'This platform/handle combination has already been submitted.' }
            });
            return;
          }

          if (result.success) {
            const data = result.data;
            
            // 조건부 라우팅
            if (data.status === 'high potential' && data.relevancy === true) {
              // 자동 협업 시작
              startCollaboration(data);
            } else if (data.status === 'needs to negotiate' && data.relevancy === true) {
              // 가격 협상
              navigate('/price-negotiation', {
                state: { responseData: data }
              });
            } else {
              // 수동 검토
              navigate('/thank-you');
            }
          } else {
            navigate('/inquiry/ssemble', {
              state: { error: result.error || 'Failed to submit inquiry' }
            });
          }
        }, 1000);

      } catch (error) {
        console.error('Error processing submission:', error);
        navigate('/inquiry/ssemble', {
          state: { error: 'Failed to submit inquiry' }
        });
      }
    };

    // 자동 협업 시작 함수
    const startCollaboration = async (responseData: any) => {
      try {
        const workflowResponse = await fetch(`${API_URL}/api/collaborations/start-auto`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            responseId: responseData.id,
            sourceType: 'auto_approved',
            agreedPrice: responseData.extractedPrice
          })
        });
        
        const workflowResult = await workflowResponse.json();
        
        if (workflowResult.success) {
          navigate('/collaboration-progress', {
            state: {
              workflowData: workflowResult.data,
              accessToken: workflowResult.accessToken
            }
          });
        } else {
          navigate('/thank-you');
        }
      } catch (error) {
        console.error('Error starting collaboration:', error);
        navigate('/thank-you');
      }
    };

    processSubmission();
  }, [navigate, submissionData]);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        textAlign: 'center',
        maxWidth: '500px',
        padding: '2rem'
      }}>
        {/* Ssemble Logo */}
        <div style={{
          marginBottom: '2rem'
        }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: '#ff6b35',
            marginBottom: '0.5rem'
          }}>
            Ssemble
          </h1>
        </div>

        {/* Animated Loading Spinner */}
        <div style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 2rem',
          position: 'relative'
        }}>
          <div style={{
            width: '100%',
            height: '100%',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #ff6b35',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>

        {/* Progress Bar */}
        <div style={{
          width: '100%',
          height: '8px',
          backgroundColor: '#f0f0f0',
          borderRadius: '4px',
          marginBottom: '1.5rem',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${progress}%`,
            height: '100%',
            backgroundColor: '#ff6b35',
            borderRadius: '4px',
            transition: 'width 0.3s ease',
            background: 'linear-gradient(90deg, #ff6b35, #ff8c42)'
          }} />
        </div>

        {/* Loading Text */}
        <h2 style={{
          fontSize: '1.5rem',
          color: '#333',
          marginBottom: '1rem',
          fontWeight: '600'
        }}>
          {loadingText}
        </h2>

        <p style={{
          fontSize: '1rem',
          color: '#666',
          lineHeight: '1.6'
        }}>
          We're analyzing your profile and determining the best collaboration approach. This may take a few moments.
        </p>

        {/* Progress Percentage */}
        <p style={{
          fontSize: '0.9rem',
          color: '#999',
          marginTop: '1rem'
        }}>
          {Math.round(progress)}% complete
        </p>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LoadingPage;